import { createAsyncThunk } from '@reduxjs/toolkit';
import Endpoints from 'constants/endpoints';
import focusBearApi, { isAbortLike } from 'services/axios-config';
import { REQUEST_ERROR_MESSAGE_KEY } from 'constants/error.constant';
import { toastRequestError } from 'utils/api-error.util';
import { validateAttachmentFile } from 'services/file-validation';
import {
  updateIsCreatingToDo,
  updateIsFetchingToDo,
  updateUpdatingTaskId,
  updateIsUpdatingToDo,
  updateShouldResetNewTaskForm,
  updateNewTodoPlayerTask,
  updateIsSearchingToDos,
  updateIsConvertingBrianDump,
  updateShowBrainDumpTasksModal,
  addReactionToComment,
  removeReactionFromComment,
  removeReactionFromTask
} from './slice';
import {
  CreateKeywordPayload,
  CreateToDoPayload,
  TaskComment
} from 'interfaces';
import { CommentReaction, TaskReaction } from 'interfaces/common.interface';
import { HTTP_STATS_CODE, PAGE, EXPORT_TASKS } from 'constants/general';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { RootState } from 'store';
import { isValidTodoTaskMergePayload } from 'utils/todo-task-sync.util';
import {
  updatePaginationCurrentPage,
  updatePaginationTotalPages
} from '../pagination/slice';
import { updateTodoSubTasks } from '../modal/slice';
import { DEFAULT_TO_DO_SUBTASKS } from 'assets/data';
import { ToDoTask } from 'interfaces/common.interface';
import { SORT_MODE, SORT_ORDER, TO_DO_STATUS } from 'enums';
import { captureException } from 'services/posthog.service';
import { setHasDataLoadFailed } from '../general/slice';
import { isApiDataValid, isRecord } from 'utils/api/api-data-validity.util';
import { DeleteKeywordPayload } from 'interfaces/argument.interace';
import {
  buildGetToDosProjectParams,
  getBrainDumpTimeout,
  refineTaskTopMetrics
} from 'utils/todo.util';
import { stripTodoPlayerTaskForPersist } from 'utils/todo-player.util';
import type { ToDoPlayerTask } from 'interfaces';

export const getToDos = createAsyncThunk(
  'to_do/get_to_dos',
  async (
    {
      page,
      append = true,
      itemsPerPage,
      status,
      replace,
      sortMode,
      omitFetchIndicator = false
    }: {
      page: number;
      append?: boolean;
      itemsPerPage?: number;
      status?: TO_DO_STATUS;
      eisenhower_quadrant?: number;
      replace?: boolean;
      sortMode?: SORT_MODE;
      /** When true, skip global loading flags (used by refreshTodosAllStatuses batch). */
      omitFetchIndicator?: boolean;
    },
    { dispatch, getState }
  ) => {
    try {
      if (!omitFetchIndicator) {
        dispatch(updateIsFetchingToDo(true));
      }
      const rootState = getState() as RootState;
      const {
        toDo: { todoFilters }
      } = rootState;
      const effectiveSortMode: SORT_MODE =
        sortMode ?? todoFilters.sortMode ?? SORT_MODE.SMART;
      const projectParams = buildGetToDosProjectParams(
        todoFilters.project,
        rootState.project.projects
      );
      page === PAGE.FIRST &&
        dispatch(updatePaginationTotalPages(PAGE.INIT_TOTAL));

      const { data } = await focusBearApi.get(Endpoints.TO_DO, {
        params: {
          page,
          take: itemsPerPage,
          ...(status ? { status } : {}),
          order: SORT_ORDER.DESC,
          sort_mode: effectiveSortMode,
          ...projectParams
        }
      });

      if (!isApiDataValid(data, isRecord)) {
        dispatch(setHasDataLoadFailed(true));
        return;
      }

      const rawList = data?.data ?? [];
      const hasNextPage = Boolean(data?.meta?.hasNextPage);
      const hasPreviousPage = Boolean(data?.meta?.hasPreviousPage);
      const rawItemCount = data?.meta?.itemCount;
      const itemCount =
        rawItemCount != null && Number.isFinite(Number(rawItemCount))
          ? Number(rawItemCount)
          : null;

      return {
        data: rawList.map((task: ToDoTask) => refineTaskTopMetrics(task)),
        append,
        status,
        replace,
        requestedPage: page,
        hasNextPage,
        hasPreviousPage,
        itemCount,
        omitFetchIndicator
      };
    } catch (error) {
      if (!omitFetchIndicator) {
        dispatch(updateIsFetchingToDo(false));
      }
      captureException(error);
    }
  }
);

/**
 * Fetches every page of to-do tasks for CSV export. Does not accept or apply
 * list filters (project, sort, status, search, or other toolbar filters) —
 * the export always reflects the user's full task set from the API. Does NOT
 * mutate to-do/pagination state. Returns `null` on failure so callers can fall
 * back to whatever is already loaded in the list view.
 */
export const getAllToDosForExport = createAsyncThunk(
  'to_do/get_all_to_dos_for_export',
  async (): Promise<{ tasks: ToDoTask[]; truncated: boolean } | null> => {
    try {
      const allTasks: ToDoTask[] = [];
      let page = PAGE.FIRST;
      let hasNextPage = true;

      while (hasNextPage && page <= EXPORT_TASKS.MAX_PAGES) {
        const { data } = await focusBearApi.get(Endpoints.TO_DO, {
          params: {
            page,
            take: EXPORT_TASKS.PER_PAGE
          }
        });

        const rawList: ToDoTask[] = data?.data ?? [];
        allTasks.push(...rawList.map((task) => refineTaskTopMetrics(task)));
        hasNextPage = Boolean(data?.meta?.hasNextPage);
        page += 1;
      }

      return { tasks: allTasks, truncated: hasNextPage };
    } catch (error) {
      captureException(error);
      return null;
    }
  }
);

/** Refetches page 1 for each status bucket (Doing, To do, Done) with current filters/sort. */
export const refreshTodosAllStatuses = createAsyncThunk(
  'to_do/refresh_all_statuses',
  async (
    opts: { itemsPerPage?: number; sortMode?: SORT_MODE } | undefined,
    { dispatch, getState }
  ) => {
    const rootState = getState() as RootState;
    const itemsPerPage =
      opts?.itemsPerPage ??
      rootState.pagination.itemsPerPage ??
      EXPORT_TASKS.PER_PAGE;
    const sortMode = opts?.sortMode ?? rootState.toDo.todoFilters.sortMode;
    const statuses: TO_DO_STATUS[] = [
      TO_DO_STATUS.IN_PROGRESS,
      TO_DO_STATUS.NOT_STARTED,
      TO_DO_STATUS.COMPLETED
    ];
    dispatch(updateIsFetchingToDo(true));
    dispatch(updatePaginationCurrentPage(PAGE.FIRST));
    try {
      for (const status of statuses) {
        await dispatch(
          getToDos({
            page: PAGE.FIRST,
            itemsPerPage,
            status,
            replace: true,
            append: false,
            sortMode,
            omitFetchIndicator: true
          })
        ).unwrap();
      }
    } finally {
      dispatch(updateIsFetchingToDo(false));
    }
  }
);

export const submitToDoFromFlow = createAsyncThunk(
  'to_do/submit_to_do_from_flow',
  async (
    {
      payload,
      shouldFetchTodo = true,
      fromToDoPlayer,
      hideToast = false
    }: {
      payload: CreateToDoPayload;
      shouldFetchTodo?: boolean;
      fromToDoPlayer?: boolean;
      hideToast?: boolean;
    },
    { dispatch, getState }
  ) => {
    try {
      const showNewTaskForm = (getState() as RootState).toDo.showNewTaskForm;
      const sortMode = (getState() as RootState).toDo.todoFilters.sortMode;
      !showNewTaskForm
        ? dispatch(updateIsUpdatingToDo(true))
        : dispatch(updateIsCreatingToDo(true));
      const { data, status } = await focusBearApi.put(Endpoints.TO_DO, payload);

      if (!fromToDoPlayer) {
        !showNewTaskForm
          ? dispatch(updateIsUpdatingToDo(false))
          : dispatch(updateIsCreatingToDo(false));
        if (status === HTTP_STATS_CODE.SUCCESS) {
          dispatch(updateShouldResetNewTaskForm(true));
          if (shouldFetchTodo) {
            dispatch(refreshTodosAllStatuses({ sortMode }));
          } else {
            dispatch(updateNewTodoPlayerTask(data));
          }
          dispatch(
            updateTodoSubTasks({
              ...DEFAULT_TO_DO_SUBTASKS,
              taskId: payload.id
            })
          );
          !hideToast &&
            toast.success(
              t(
                `to_do_procrastinate.${!showNewTaskForm ? 'task_updated_successfully' : 'task_created_successfully'}`
              )
            );
        } else {
          !hideToast &&
            toast.error(
              t(
                `to_do_procrastinate.${!showNewTaskForm ? 'could_not_update_the_task' : 'could_not_create_the_task'}`
              )
            );
        }
      } else if (!hideToast && status !== HTTP_STATS_CODE.SUCCESS) {
        toast.error(
          t('to_do_procrastinate.could_not_update_the_task', {
            title: payload.title
          })
        );
      }
      if (
        status === HTTP_STATS_CODE.SUCCESS &&
        isValidTodoTaskMergePayload(data)
      ) {
        return data;
      }
      return null;
    } catch (error) {
      captureException(error);
      if (fromToDoPlayer && !hideToast) {
        toast.error(
          t('to_do_procrastinate.could_not_update_the_task', {
            title: payload.title
          })
        );
      }
      return null;
    } finally {
      dispatch(updateIsCreatingToDo(false));
      dispatch(updateIsUpdatingToDo(false));
      dispatch(updateShouldResetNewTaskForm(false));
    }
  }
);

export const deleteToDo = createAsyncThunk(
  'to_do/delete_to_do',
  async (task_id: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const sortMode = (getState() as RootState).toDo.todoFilters.sortMode;
      const { status } = await focusBearApi.delete(Endpoints.TO_DO, {
        params: {
          todo_id: task_id
        }
      });
      if (status !== HTTP_STATS_CODE.SUCCESS) {
        return rejectWithValue(undefined);
      }
      // The fulfilled reducer removes the task from local state immediately;
      // the refresh keeps bucket counts/pagination in sync in the background.
      dispatch(refreshTodosAllStatuses({ sortMode }));
      return task_id;
    } catch (error) {
      captureException(error);
      return rejectWithValue(undefined);
    }
  }
);

export const persistToDoTask = createAsyncThunk(
  'to_do/persist_to_do_task',
  async (
    {
      todoTask,
      isTodoPlayer = false
    }: { todoTask: ToDoTask; isTodoPlayer?: boolean },
    { dispatch, rejectWithValue }
  ) => {
    const taskToPersist = isTodoPlayer
      ? stripTodoPlayerTaskForPersist(todoTask as ToDoPlayerTask)
      : todoTask;

    try {
      // updateUpdatingTaskId keeps the global isTaskUpdating flag in sync and
      // tracks each in-flight task by id, so concurrent completions don't clear
      // each other's per-row spinner.
      dispatch(updateUpdatingTaskId({ id: todoTask.id, isUpdating: true }));
      const { status, data } = await focusBearApi.put(
        Endpoints.TO_DO,
        taskToPersist
      );
      if (status !== HTTP_STATS_CODE.SUCCESS) {
        toast.error(
          t('to_do_procrastinate.could_not_update_the_task', {
            title: todoTask.title
          })
        );
        return rejectWithValue(todoTask);
      }

      const updatedTask = data ?? todoTask;

      return updatedTask;
    } catch (error) {
      captureException(error);
      toast.error(
        t('to_do_procrastinate.could_not_update_the_task', {
          title: todoTask.title
        })
      );
      return rejectWithValue(todoTask);
    } finally {
      dispatch(updateUpdatingTaskId({ id: todoTask.id, isUpdating: false }));
    }
  }
);

export const updateCalendarStatus = createAsyncThunk(
  'to_do/update_calendar_status',
  async ({ id, is_selected }: { id: string; is_selected: boolean }) => {
    try {
      const { status } = await focusBearApi.put(Endpoints.CALENDAR_UPDATE, {
        body: { id, is_selected }
      });
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('calendar_status.calendar_status_saved'));
      } else {
        toast.warning(t('calendar_status.calendar_status_failed'));
      }
    } catch (error) {
      captureException(error);
    }
  }
);

export const fetchCalendarData = createAsyncThunk(
  'to_do/fetch_Calendar_Data',
  async (platform: string) => {
    try {
      const { data } = await focusBearApi.get(
        Endpoints.CALENDAR_FETCH_DATA(platform)
      );
      return { data, platform };
    } catch (error) {
      captureException(error);
    }
  }
);

export const createKeyword = createAsyncThunk(
  'to_do/create_keyword',
  async (payload: CreateKeywordPayload, { dispatch }) => {
    try {
      const { status } = await focusBearApi.put(
        Endpoints.CALENDAR_KEYWORD_CREATE,
        {
          body: {
            keyword: payload.keyword,
            platform: payload.platform.toLowerCase(),
            title: payload.title,
            description: payload.description
          }
        }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(fetchCalendarData(payload.platform));
        toast.success(t('calendar_status.create_new_keyowrd_success'));
      } else {
        toast.warning(t('calendar_status.create_new_keyowrd_failed'));
      }
    } catch (error) {
      captureException(error);
    }
  }
);

export const updateKeywordStatus = createAsyncThunk(
  'to_do/update_keyword_status',
  async (payload: CreateKeywordPayload, { dispatch }) => {
    try {
      const { status } = await focusBearApi.put(
        Endpoints.CALENDAR_KEYWORD_UPDATE,
        {
          body: {
            id: payload.id,
            keyword: payload.keyword,
            title: payload.title,
            description: payload.description
          }
        }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(fetchCalendarData(payload.platform));
        toast.success(t('calendar_status.calendar_keyword_status_saved'));
      } else {
        toast.warning(t('calendar_status.calendar_keyword_status_failed'));
      }
    } catch (error) {
      captureException(error);
    }
  }
);

export const deleteKeyword = createAsyncThunk(
  'to_do/delete_keyword',
  async ({ id, platform }: DeleteKeywordPayload, { dispatch }) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.CALENDAR_KEYWORD_DELETE,
        {
          params: { id }
        }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(fetchCalendarData(platform));
        toast.success(t('calendar_status.delete_calendar_keyword_success'));
      } else {
        toast.warning(t('calendar_status.delete_calendar_keyword_failed'));
      }
    } catch (error) {
      captureException(error);
    }
  }
);

export const searchToDos = createAsyncThunk(
  'to_do/search_toDos',
  async (
    payload: {
      title: string;
      take?: number;
      active_tab?: number;
    },
    { dispatch, signal }
  ) => {
    try {
      dispatch(updateIsSearchingToDos(true));
      const { data } = await focusBearApi.get(Endpoints.TO_DO_SEARCH, {
        params: {
          title: payload.title,
          take: payload.take
        },
        signal
      });
      const tasks = data ?? [];

      return tasks;
    } catch (error) {
      dispatch(updateIsSearchingToDos(false));
      if (isAbortLike(error)) {
        throw error;
      }
      captureException(error);
      return null;
    }
  }
);

export const convertBrianDumpToTasks = createAsyncThunk(
  'to_do/create_brian_dump_to_tasks',
  async (brianDump: string, { dispatch }) => {
    try {
      dispatch(updateIsConvertingBrianDump(true));
      const timeout = getBrainDumpTimeout(brianDump.length);
      const { status, data } = await focusBearApi.post(
        Endpoints.TO_DO_BRAIN_DUMP,
        {
          contents: brianDump
        },
        {
          timeout
        }
      );
      status === HTTP_STATS_CODE.CREATED && data
        ? dispatch(updateShowBrainDumpTasksModal(true))
        : toast.error(
            t(
              'to_do_procrastinate.sorry_unable_to_convert_brain_dumps_please_try_again'
            )
          );
      return data ?? [];
    } catch (error) {
      toastRequestError(error, {
        fallbackKey: REQUEST_ERROR_MESSAGE_KEY.BRAIN_DUMP_FAILED,
        timeoutKey: REQUEST_ERROR_MESSAGE_KEY.BRAIN_DUMP_TIMEOUT
      });
      dispatch(updateIsConvertingBrianDump(false));
      captureException(error);
    }
  }
);

const extractCommentsFromResponse = (data: unknown): TaskComment[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.comments)) return obj.comments;
  }
  return [];
};

export const fetchTaskComments = createAsyncThunk(
  'to_do/fetch_task_comments',
  async (taskId: string) => {
    try {
      const { data } = await focusBearApi.get(Endpoints.TASK_COMMENTS(taskId));
      const comments = extractCommentsFromResponse(data);

      // Fetch reactions for each comment since the API doesn't include them
      const reactionPromises = comments.map(async (comment) => {
        try {
          const { data: reactionsData } = await focusBearApi.get(
            Endpoints.COMMENT_REACTIONS(taskId, comment.id)
          );
          return {
            commentId: comment.id,
            reactions: Array.isArray(reactionsData) ? reactionsData : []
          };
        } catch (error) {
          console.error(
            `Failed to fetch reactions for comment ${comment.id}:`,
            error
          );
          return {
            commentId: comment.id,
            reactions: []
          };
        }
      });

      const results = await Promise.all(reactionPromises);
      const commentsWithReactions = comments.map((comment) => {
        const reactionResult = results.find((r) => r.commentId === comment.id);
        return {
          ...comment,
          reactions: reactionResult?.reactions || []
        };
      });

      return { taskId, comments: commentsWithReactions };
    } catch (error) {
      captureException(error);
      return { taskId, comments: [] };
    }
  }
);

export const createTaskComment = createAsyncThunk(
  'to_do/create_task_comment',
  async (
    { taskId, content }: { taskId: string; content: string },
    { dispatch }
  ) => {
    try {
      const { data, status } = await focusBearApi.post<TaskComment>(
        Endpoints.TASK_COMMENTS(taskId),
        { content }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('to_do_procrastinate.comment_added'));
        dispatch(fetchTaskComments(taskId));
      }
      return data;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_add_comment'));
      captureException(error);
      return null;
    }
  }
);

export const updateTaskComment = createAsyncThunk(
  'to_do/update_task_comment',
  async (
    {
      taskId,
      commentId,
      content
    }: { taskId: string; commentId: string; content: string },
    { dispatch }
  ) => {
    try {
      const { data, status } = await focusBearApi.put<TaskComment>(
        Endpoints.TASK_COMMENT_BY_ID(taskId, commentId),
        { content }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('to_do_procrastinate.comment_updated'));
        dispatch(fetchTaskComments(taskId));
      }
      return data;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_update_comment'));
      captureException(error);
      return null;
    }
  }
);

export const deleteTaskComment = createAsyncThunk(
  'to_do/delete_task_comment',
  async (
    { taskId, commentId }: { taskId: string; commentId: string },
    { dispatch }
  ) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.TASK_COMMENT_BY_ID(taskId, commentId)
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('to_do_procrastinate.comment_deleted'));
        dispatch(fetchTaskComments(taskId));
      }
      return status === HTTP_STATS_CODE.SUCCESS;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_delete_comment'));
      captureException(error);
      return false;
    }
  }
);

export const addCommentReaction = createAsyncThunk(
  'to_do/add_comment_reaction',
  async (
    {
      taskId,
      commentId,
      emoji
    }: { taskId: string; commentId: string; emoji: string },
    { dispatch, getState }
  ) => {
    const currentUserId = (getState() as RootState).user.details?.id ?? '';
    const tempId = `temp-${Date.now()}`;

    if (currentUserId) {
      const tempReaction: CommentReaction = {
        id: tempId,
        comment_id: commentId,
        user_id: currentUserId,
        emoji,
        created_at: new Date().toISOString()
      };
      dispatch(
        addReactionToComment({ taskId, commentId, reaction: tempReaction })
      );
    }

    try {
      const { status } = await focusBearApi.post(
        Endpoints.COMMENT_REACTIONS(taskId, commentId),
        { emoji }
      );
      const isSuccess =
        status === HTTP_STATS_CODE.CREATED ||
        status === HTTP_STATS_CODE.SUCCESS;
      if (isSuccess) {
        dispatch(fetchTaskComments(taskId));
      } else if (currentUserId) {
        dispatch(
          removeReactionFromComment({ taskId, commentId, reactionId: tempId })
        );
      }
      return isSuccess;
    } catch (error) {
      if (currentUserId) {
        dispatch(
          removeReactionFromComment({ taskId, commentId, reactionId: tempId })
        );
      }
      toast.error(t('to_do_procrastinate.could_not_add_reaction'));
      captureException(error);
      return false;
    }
  }
);

export const removeCommentReaction = createAsyncThunk(
  'to_do/remove_comment_reaction',
  async (
    {
      taskId,
      commentId,
      emoji
    }: { taskId: string; commentId: string; emoji: string },
    { dispatch, getState }
  ) => {
    const currentUserId = (getState() as RootState).user.details?.id;
    dispatch(
      removeReactionFromComment({ taskId, commentId, emoji, currentUserId })
    );

    try {
      const { status } = await focusBearApi.delete(
        Endpoints.COMMENT_REACTIONS(taskId, commentId),
        { params: { emoji } }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(fetchTaskComments(taskId));
      }
      return status === HTTP_STATS_CODE.SUCCESS;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_remove_reaction'));
      captureException(error);
      return false;
    }
  }
);

export const fetchToDosByIds = async (ids: string[]): Promise<ToDoTask[]> => {
  if (ids.length === 0) {
    return [];
  }
  try {
    const { data } = await focusBearApi.get(Endpoints.TO_DO_BY_IDS, {
      params: { ids: ids.join(',') }
    });
    return data ?? [];
  } catch (error) {
    captureException(error);
    return [];
  }
};

const extractReactionsFromResponse = (data: unknown): TaskReaction[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.reactions)) return obj.reactions;
  }
  return [];
};

export const fetchTaskReactions = createAsyncThunk(
  'to_do/fetch_task_reactions',
  async (taskId: string) => {
    try {
      const { data } = await focusBearApi.get(Endpoints.TASK_REACTIONS(taskId));
      const reactions = extractReactionsFromResponse(data);
      return { taskId, reactions };
    } catch (error) {
      captureException(error);
      return { taskId, reactions: [] };
    }
  }
);

export const addTaskReaction = createAsyncThunk(
  'to_do/add_task_reaction',
  async (
    { taskId, emoji }: { taskId: string; emoji: string },
    { dispatch }
  ) => {
    try {
      const { status } = await focusBearApi.post(
        Endpoints.TASK_REACTIONS(taskId),
        { emoji }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        dispatch(fetchTaskReactions(taskId));
        dispatch(refreshTodosAllStatuses());
      }
      return status === HTTP_STATS_CODE.CREATED;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_add_reaction'));
      captureException(error);
      return false;
    }
  }
);

export const removeTaskReaction = createAsyncThunk(
  'to_do/remove_task_reaction',
  async (
    { taskId, emoji }: { taskId: string; emoji: string },
    { dispatch, getState }
  ) => {
    const currentUserId = (getState() as RootState).user.details?.id;
    dispatch(removeReactionFromTask({ taskId, emoji, currentUserId }));
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.TASK_REACTIONS(taskId),
        { params: { emoji } }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(refreshTodosAllStatuses());
      }
      return status === HTTP_STATS_CODE.SUCCESS;
    } catch (error) {
      dispatch(fetchTaskReactions(taskId));
      toast.error(t('to_do_procrastinate.could_not_remove_reaction'));
      captureException(error);
      return false;
    }
  }
);

export const fetchTaskAttachments = createAsyncThunk(
  'to_do/fetch_task_attachments',
  async (taskId: string) => {
    try {
      const { data } = await focusBearApi.get(
        Endpoints.TASK_ATTACHMENTS(taskId)
      );
      return { taskId, attachments: data ?? [] };
    } catch (error) {
      captureException(error);
      return { taskId, attachments: [] };
    }
  }
);

export const uploadTaskAttachment = createAsyncThunk(
  'to_do/upload_task_attachment',
  async ({ taskId, file }: { taskId: string; file: File }, { dispatch }) => {
    try {
      const fileValidation = await validateAttachmentFile(file);
      if (!fileValidation.valid) {
        toast.error(
          t(
            fileValidation.error ??
              'to_do_procrastinate.could_not_upload_attachment'
          )
        );
        return false;
      }

      const { data: uploadData } = await focusBearApi.post(
        Endpoints.TASK_ATTACHMENT_UPLOAD_URL(taskId),
        { file_name: file.name, content_type: file.type }
      );

      if (!uploadData?.uploadUrl || !uploadData?.fileKey) {
        throw new Error('Failed to get upload URL');
      }

      await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      const { status } = await focusBearApi.post(
        Endpoints.TASK_ATTACHMENTS(taskId),
        {
          file_name: file.name,
          file_key: uploadData.fileKey,
          content_type: file.type,
          file_size: file.size
        }
      );

      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('to_do_procrastinate.attachment_uploaded'));
        dispatch(fetchTaskAttachments(taskId));
      }
      return status === HTTP_STATS_CODE.CREATED;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_upload_attachment'));
      captureException(error);
      return false;
    }
  }
);

export const deleteTaskAttachment = createAsyncThunk(
  'to_do/delete_task_attachment',
  async (
    { taskId, attachmentId }: { taskId: string; attachmentId: string },
    { dispatch }
  ) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.TASK_ATTACHMENT_BY_ID(taskId, attachmentId)
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('to_do_procrastinate.attachment_deleted'));
        dispatch(fetchTaskAttachments(taskId));
      }
      return status === HTTP_STATS_CODE.SUCCESS;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_delete_attachment'));
      captureException(error);
      return false;
    }
  }
);

export const fetchCommentAttachments = createAsyncThunk(
  'to_do/fetch_comment_attachments',
  async ({ taskId, commentId }: { taskId: string; commentId: string }) => {
    try {
      const { data } = await focusBearApi.get(
        Endpoints.COMMENT_ATTACHMENTS(taskId, commentId)
      );
      return { commentId, attachments: data ?? [] };
    } catch (error) {
      captureException(error);
      return { commentId, attachments: [] };
    }
  }
);

export const uploadCommentAttachment = createAsyncThunk(
  'to_do/upload_comment_attachment',
  async (
    {
      taskId,
      commentId,
      file
    }: { taskId: string; commentId: string; file: File },
    { dispatch }
  ) => {
    try {
      const fileValidation = await validateAttachmentFile(file);
      if (!fileValidation.valid) {
        toast.error(
          t(
            fileValidation.error ??
              'to_do_procrastinate.could_not_upload_attachment'
          )
        );
        return false;
      }

      const { data: uploadData } = await focusBearApi.post(
        Endpoints.COMMENT_ATTACHMENT_UPLOAD_URL(taskId, commentId),
        { file_name: file.name, content_type: file.type }
      );

      if (!uploadData?.uploadUrl || !uploadData?.fileKey) {
        throw new Error('Failed to get upload URL');
      }

      await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      const { status } = await focusBearApi.post(
        Endpoints.COMMENT_ATTACHMENTS(taskId, commentId),
        {
          file_name: file.name,
          file_key: uploadData.fileKey,
          content_type: file.type,
          file_size: file.size
        }
      );

      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('to_do_procrastinate.attachment_uploaded'));
        dispatch(fetchCommentAttachments({ taskId, commentId }));
      }
      return status === HTTP_STATS_CODE.CREATED;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_upload_attachment'));
      captureException(error);
      return false;
    }
  }
);

export const deleteCommentAttachment = createAsyncThunk(
  'to_do/delete_comment_attachment',
  async (
    {
      taskId,
      commentId,
      attachmentId
    }: { taskId: string; commentId: string; attachmentId: string },
    { dispatch }
  ) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.COMMENT_ATTACHMENT_BY_ID(commentId, attachmentId)
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('to_do_procrastinate.attachment_deleted'));
        dispatch(fetchCommentAttachments({ taskId, commentId }));
      }
      return status === HTTP_STATS_CODE.SUCCESS;
    } catch (error) {
      toast.error(t('to_do_procrastinate.could_not_delete_attachment'));
      captureException(error);
      return false;
    }
  }
);
